/**
 * Enhanced Notification System
 * Provides comprehensive user feedback with different notification types and queuing
 */

// Notification types and their configurations
export const NOTIFICATION_TYPES = {
  SUCCESS: {
    type: 'success',
    icon: '✅',
    className: 'notification-success',
    duration: 3000
  },
  ERROR: {
    type: 'error',
    icon: '❌',
    className: 'notification-error',
    duration: 5000
  },
  WARNING: {
    type: 'warning',
    icon: '⚠️',
    className: 'notification-warning',
    duration: 4000
  },
  INFO: {
    type: 'info',
    icon: 'ℹ️',
    className: 'notification-info',
    duration: 3000
  },
  LOADING: {
    type: 'loading',
    icon: '⏳',
    className: 'notification-loading',
    duration: 0 // Persistent until dismissed
  }
};

/**
 * Notification System class
 */
export class NotificationSystem {
  constructor() {
    this.notifications = new Map();
    this.queue = [];
    this.maxVisible = 5;
    this.container = null;
    this.initialized = false;
  }

  /**
   * Initialize the notification system
   */
  init() {
    if (this.initialized) return;

    this.createContainer();
    this.initialized = true;
  }

  /**
   * Create notification container if it doesn't exist
   */
  createContainer() {
    this.container = document.getElementById('notification-container');

    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'notification-container';
      this.container.className = 'notification-container';
      document.body.appendChild(this.container);
    }
  }

  /**
   * Show a notification
   */
  show(message, type = 'info', options = {}) {
    this.init();

    const notificationConfig = NOTIFICATION_TYPES[type.toUpperCase()] || NOTIFICATION_TYPES.INFO;
    const notificationId = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const notification = {
      id: notificationId,
      message,
      type: notificationConfig.type,
      icon: options.icon || notificationConfig.icon,
      className: notificationConfig.className,
      duration: options.duration !== undefined ? options.duration : notificationConfig.duration,
      persistent: options.persistent || false,
      actions: options.actions || [],
      progress: options.progress,
      timestamp: new Date(),
      ...options
    };

    // Add to notifications map
    this.notifications.set(notificationId, notification);

    // Create DOM element
    this.createNotificationElement(notification);

    // Auto-dismiss if not persistent and has duration
    if (!notification.persistent && notification.duration > 0) {
      setTimeout(() => {
        this.dismiss(notificationId);
      }, notification.duration);
    }

    // Manage visible count
    this.manageVisibleNotifications();

    return notificationId;
  }

  /**
   * Create DOM element for notification
   */
  createNotificationElement(notification) {
    const element = document.createElement('div');
    element.id = `notification-${notification.id}`;
    element.className = `notification ${notification.className}`;
    element.setAttribute('data-notification-id', notification.id);

    // Build content
    let content = `
      <div class="notification-content">
        <div class="notification-header">
          <span class="notification-icon">${notification.icon}</span>
          <span class="notification-title">${notification.title || this.getTypeTitle(notification.type)}</span>
          <button class="notification-close" data-action="close" aria-label="Close notification">×</button>
        </div>
        <div class="notification-message">${notification.message}</div>
    `;

    // Add progress bar if specified
    if (notification.progress !== undefined) {
      content += `
        <div class="notification-progress">
          <div class="notification-progress-bar" style="width: ${notification.progress}%"></div>
        </div>
      `;
    }

    // Add actions if specified
    if (notification.actions && notification.actions.length > 0) {
      content += '<div class="notification-actions">';
      notification.actions.forEach(action => {
        content += `
          <button class="notification-action" data-action="${action.action}" data-notification-id="${notification.id}">
            ${action.label}
          </button>
        `;
      });
      content += '</div>';
    }

    content += '</div>';
    element.innerHTML = content;

    // Add event listeners
    this.attachEventListeners(element, notification);

    // Add to container with animation
    element.style.opacity = '0';
    element.style.transform = 'translateY(-20px)';
    this.container.appendChild(element);

    // Animate in
    setTimeout(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    }, 10);

    return element;
  }

  /**
   * Attach event listeners to notification element
   */
  attachEventListeners(element, notification) {
    // Close button
    const closeButton = element.querySelector('[data-action="close"]');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.dismiss(notification.id);
      });
    }

    // Action buttons
    const actionButtons = element.querySelectorAll('.notification-action');
    actionButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const action = e.target.getAttribute('data-action');
        const notificationId = e.target.getAttribute('data-notification-id');
        this.handleAction(action, notificationId, notification);
      });
    });

    // Auto-dismiss on click (for non-persistent notifications without actions)
    if (!notification.persistent && (!notification.actions || notification.actions.length === 0)) {
      element.addEventListener('click', () => {
        this.dismiss(notification.id);
      });
    }
  }

  /**
   * Handle notification action
   */
  handleAction(actionName, notificationId, notification) {
    const action = notification.actions?.find(a => a.action === actionName);
    if (action && action.handler) {
      action.handler(notificationId, notification);
    }

    // Dismiss notification unless action specifies to keep it
    if (!action?.keepOpen) {
      this.dismiss(notificationId);
    }
  }

  /**
   * Update notification progress
   */
  updateProgress(notificationId, progress, message) {
    const notification = this.notifications.get(notificationId);
    if (!notification) return;

    notification.progress = progress;
    if (message) {
      notification.message = message;
    }

    const element = document.getElementById(`notification-${notification.id}`);
    if (element) {
      // Update progress bar
      const progressBar = element.querySelector('.notification-progress-bar');
      if (progressBar) {
        progressBar.style.width = `${progress}%`;
      }

      // Update message
      if (message) {
        const messageElement = element.querySelector('.notification-message');
        if (messageElement) {
          messageElement.textContent = message;
        }
      }
    }
  }

  /**
   * Dismiss a notification
   */
  dismiss(notificationId) {
    const notification = this.notifications.get(notificationId);
    if (!notification) return;

    const element = document.getElementById(`notification-${notification.id}`);
    if (element) {
      // Animate out
      element.style.opacity = '0';
      element.style.transform = 'translateY(-20px)';

      setTimeout(() => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      }, 300);
    }

    this.notifications.delete(notificationId);
  }

  /**
   * Dismiss all notifications
   */
  dismissAll() {
    const notificationIds = Array.from(this.notifications.keys());
    notificationIds.forEach(id => this.dismiss(id));
  }

  /**
   * Manage visible notifications count
   */
  manageVisibleNotifications() {
    const visibleCount = this.notifications.size;
    if (visibleCount > this.maxVisible) {
      // Dismiss oldest non-persistent notifications
      const sortedNotifications = Array.from(this.notifications.values())
        .sort((a, b) => a.timestamp - b.timestamp);

      const toRemove = visibleCount - this.maxVisible;
      let removed = 0;

      for (const notification of sortedNotifications) {
        if (removed >= toRemove) break;
        if (!notification.persistent) {
          this.dismiss(notification.id);
          removed++;
        }
      }
    }
  }

  /**
   * Get default title for notification type
   */
  getTypeTitle(type) {
    const titles = {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
      loading: 'Loading'
    };
    return titles[type] || 'Notification';
  }

  /**
   * Show error notification with enhanced error object
   */
  showError(error, options = {}) {
    let message = error.message || 'An error occurred';
    let title = 'Error';
    const actions = [];

    // Handle enhanced errors
    if (error.type && error.suggestedAction) {
      if (error.retryable && options.onRetry) {
        actions.push({
          action: 'retry',
          label: 'Retry',
          handler: options.onRetry
        });
      }

      if (error.suggestedAction && !actions.some(a => a.action === 'retry')) {
        // Add help action if we have suggestions
        actions.push({
          action: 'help',
          label: 'Help',
          handler: () => {
            this.show(error.suggestedAction, 'info', {
              title: 'Suggested Action',
              duration: 8000
            });
          }
        });
      }
    }

    return this.show(message, 'error', {
      title,
      actions,
      persistent: error.severity === 'critical',
      ...options
    });
  }

  /**
   * Show success notification
   */
  showSuccess(message, options = {}) {
    return this.show(message, 'success', {
      title: 'Success',
      ...options
    });
  }

  /**
   * Show warning notification
   */
  showWarning(message, options = {}) {
    return this.show(message, 'warning', {
      title: 'Warning',
      ...options
    });
  }

  /**
   * Show info notification
   */
  showInfo(message, options = {}) {
    return this.show(message, 'info', {
      title: 'Information',
      ...options
    });
  }

  /**
   * Show loading notification
   */
  showLoading(message, options = {}) {
    return this.show(message, 'loading', {
      title: 'Loading',
      persistent: true,
      progress: options.progress || 0,
      ...options
    });
  }

  /**
   * Get active notifications
   */
  getActive() {
    return Array.from(this.notifications.values());
  }

  /**
   * Get notifications by type
   */
  getByType(type) {
    return Array.from(this.notifications.values())
      .filter(n => n.type === type);
  }
}

// Export singleton instance
export const notificationSystem = new NotificationSystem();

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    notificationSystem.init();
  });
} else {
  notificationSystem.init();
}